import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Camera, FileText, Upload, Loader2, ArrowRight, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * DocumentScannerBox — Componente condiviso per scansione documenti AI (GPT-4o Vision).
 * Layout coerente con quello dei Contratti, usato in TUTTE le sezioni che richiedono
 * documenti d'identità (clienti, conducenti aggiuntivi, contratti, ecc.).
 *
 * Props:
 *  - onResult(payload)  : callback con { persona, documento, tipo, raw } dopo OCR
 *  - title              : titolo del banner (default "Scansione AI documenti")
 *  - subtitle           : sottotitolo descrittivo
 *  - accept             : MIME types accettati (default foto + PDF)
 *  - testIdPrefix       : prefisso data-testid per testing automation
 *  - variant            : 'full' (con dropzone) | 'compact' (solo 2 pulsanti)
 *  - hint               : testo di hint sotto i pulsanti (default standard descrittivo)
 */
export default function DocumentScannerBox({
  onResult,
  title = "Scansione AI documenti",
  subtitle = "AUTO-COMPILA",
  description = "Carica foto patente, CI, passaporto, libretto, voucher, contratti precedenti o PDF di qualsiasi tipo. L'AI estrae i dati e compila il modulo in autonomia.",
  testIdPrefix = "scanner",
  variant = "full",
  context = null, // modulo corrente per Smart Router AI: customers|contracts|multe|fleet|sinistri|damages|bookings
}) {
  const [scanning, setScanning] = useState(false);
  const [routerHint, setRouterHint] = useState(null);
  const navigate = useNavigate();

  const onFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setScanning(true);
    setRouterHint(null);
    try {
      const fd = new FormData();
      Array.from(fileList).slice(0, 10).forEach((f) => fd.append("files", f));
      if (context) fd.append("context", context);
      const r = await api.post("/contracts/scan-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onResult?.(r.data);
      if (r.data?.smart_router?.mismatch) setRouterHint(r.data.smart_router);
      const tipo = r.data?.documento?.tipo;
      const tipoLabel = tipo === "patente" ? "Patente"
        : ["ci", "carta_identita"].includes(tipo) ? "Carta d'identità"
        : tipo === "passaporto" ? "Passaporto"
        : tipo === "libretto" ? "Libretto"
        : "Documento";
      toast.success(`✨ ${tipoLabel} estratto! Verifica i campi popolati.`);
    } catch (e) {
      toast.error("Errore OCR: " + (e?.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  return (
    <div className="p-3 rounded-xl bg-fuchsia-500/[0.06] border border-fuchsia-500/30" data-testid={`${testIdPrefix}-box`}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-fuchsia-300" />
        <div className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-300">{title}</div>
        <div className="text-[10px] text-white/45 ml-auto">{subtitle}</div>
      </div>
      <p className="text-[11px] text-white/55 mb-3 leading-relaxed">{description}</p>

      {/* 2 pulsanti espliciti: foto vs PDF */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <label className={`flex items-center justify-center gap-2 cursor-pointer rounded-lg py-2.5 text-xs uppercase tracking-wider transition border ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06] text-fuchsia-300 cursor-wait" : "border-white/15 bg-white/[0.03] hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.06] text-white/80"}`}
          data-testid={`${testIdPrefix}-photo`}>
          <input type="file" multiple accept="image/*" capture="environment"
            className="hidden" onChange={(e) => onFiles(e.target.files)} disabled={scanning} />
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          📷 Scatta foto / galleria
        </label>
        <label className={`flex items-center justify-center gap-2 cursor-pointer rounded-lg py-2.5 text-xs uppercase tracking-wider transition border ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06] text-fuchsia-300 cursor-wait" : "border-white/15 bg-white/[0.03] hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.06] text-white/80"}`}
          data-testid={`${testIdPrefix}-pdf`}>
          <input type="file" multiple accept="application/pdf,.pdf"
            className="hidden" onChange={(e) => onFiles(e.target.files)} disabled={scanning} />
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          📄 Carica PDF
        </label>
      </div>

      {variant === "full" && (
        <label className={`block cursor-pointer rounded-xl border-2 border-dashed transition p-3 text-center ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06]" : "border-white/15 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.04]"}`}
          data-testid={`${testIdPrefix}-dropzone`}>
          <input type="file" multiple accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic"
            className="hidden" onChange={(e) => onFiles(e.target.files)}
            data-testid={`${testIdPrefix}-input`} disabled={scanning} />
          {scanning ? (
            <div className="inline-flex items-center gap-2 text-fuchsia-300 text-sm">
              <Loader2 size={16} className="animate-spin" /> Analisi AI in corso…
            </div>
          ) : (
            <div>
              <Upload size={18} className="mx-auto text-white/40 mb-1" />
              <div className="text-xs text-white/70">…oppure trascina qui <b>più file insieme</b> (foto + PDF mescolati)</div>
              <div className="text-[10px] text-white/35 mt-0.5">Max 10 file · 12MB l'uno · auto-OCR GPT-4o Vision</div>
            </div>
          )}
        </label>
      )}
      {/* Smart Router AI: documento appartiene a un altro modulo */}
      {routerHint && (
        <div className="mt-2 p-3 rounded-lg bg-amber-500/[0.08] border border-amber-500/40" data-testid={`${testIdPrefix}-router-hint`}>
          <div className="text-[11px] text-amber-200 leading-relaxed mb-2">{routerHint.message}</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`${testIdPrefix}-router-go`}
              onClick={() => navigate(routerHint.suggested_route)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/50 hover:bg-amber-500/30 transition"
            >
              Vai a {routerHint.suggested_label} <ArrowRight size={12} />
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-router-dismiss`}
              onClick={() => setRouterHint(null)}
              className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-lg text-white/50 hover:text-white border border-white/10 hover:border-white/25 transition"
            >
              <X size={11} /> Resta qui
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Helper: applica i dati OCR al form Customer (riusabile per Anagrafica, Patente, CI tabs).
 */
export function applyOcrToCustomerForm(setForm, payload) {
  const persona = payload?.persona || {};
  const doc = payload?.documento || {};
  const tipo = doc.tipo;
  setForm((f) => ({
    ...f,
    nome: persona.nome || f.nome,
    cognome: persona.cognome || f.cognome,
    codice_fiscale: (persona.codice_fiscale || f.codice_fiscale || "").toUpperCase(),
    email: persona.email || f.email,
    telefono: persona.telefono || f.telefono,
    ragione_sociale: persona.ragione_sociale || f.ragione_sociale,
    piva: persona.piva || f.piva,
    indirizzo: persona.indirizzo || f.indirizzo,
    citta: persona.citta || f.citta,
    cap: persona.cap || f.cap,
    provincia: persona.provincia || f.provincia,
    // Patente
    patente_numero: tipo === "patente" ? (doc.numero || f.patente_numero) : f.patente_numero,
    patente_emissione: tipo === "patente" ? (doc.data_emissione || f.patente_emissione) : f.patente_emissione,
    patente_scadenza: tipo === "patente" ? (doc.data_scadenza || f.patente_scadenza) : f.patente_scadenza,
    patente_comune: tipo === "patente" ? (doc.ente_emissione || f.patente_comune) : f.patente_comune,
    patente_categoria: tipo === "patente" ? (doc.categoria_patente || f.patente_categoria) : f.patente_categoria,
    // Carta d'identità
    ci_numero: ["ci", "carta_identita"].includes(tipo) ? (doc.numero || f.ci_numero) : f.ci_numero,
    ci_emissione: ["ci", "carta_identita"].includes(tipo) ? (doc.data_emissione || f.ci_emissione) : f.ci_emissione,
    ci_scadenza: ["ci", "carta_identita"].includes(tipo) ? (doc.data_scadenza || f.ci_scadenza) : f.ci_scadenza,
    ci_comune: ["ci", "carta_identita"].includes(tipo) ? (doc.ente_emissione || f.ci_comune) : f.ci_comune,
  }));
}
